// src/adminDashboard/shared/components/LazyImage.tsx
// Lazy loading image component with placeholder

import React, { useState, memo } from 'react';

import useIntersectionObserver from '../hooks/useIntersectionObserver';

interface LazyImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt?: string;
    className?: string;
    placeholder?: React.ReactNode;
    fallback?: React.ReactNode;
    width?: string | number;
    height?: string | number;
}

const LazyImage = memo(({
    src,
    alt = '',
    className = '',
    placeholder = null,
    fallback = null,
    width,
    height,
    ...props
}: LazyImageProps) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    const { ref, hasIntersected } = useIntersectionObserver({ rootMargin: '100px' });

    const handleLoad = () => {
        setIsLoaded(true);
    };

    const handleError = () => {
        setHasError(true);
    };

    // Default placeholder
    const defaultPlaceholder = (
        <div
            className={`animate-pulse bg-slate-200 ${className}`}
            style={{ width, height }}
        />
    );

    // Default fallback (error state)
    const defaultFallback = (
        <div
            className={`flex items-center justify-center bg-slate-100 text-slate-400 ${className}`}
            style={{ width, height }}
        >
            <span className="text-xs">Failed to load</span>
        </div>
    );

    if (hasError) {
        return <>{fallback || defaultFallback}</>;
    }

    return (
        <div ref={ref} className="relative" style={{ width, height }}>
            {/* Placeholder while loading */}
            {(!hasIntersected || !isLoaded) && (placeholder || defaultPlaceholder)}

            {/* Actual image - only load when in viewport */}
            {hasIntersected && (
                <img
                    src={src}
                    alt={alt}
                    onLoad={handleLoad}
                    onError={handleError}
                    className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
                    style={{
                        width,
                        height,
                        position: isLoaded ? 'relative' : 'absolute',
                        top: 0,
                        left: 0,
                    }}
                    loading="lazy"
                    {...props}
                />
            )}
        </div>
    );
});

LazyImage.displayName = 'LazyImage';

export default LazyImage;
